import React, { useEffect, useState } from "react";
import { Puff } from "react-loader-spinner";
import { useAuth } from "../../components/AuthContext/AuthContext";

import { User, Edit, Phone, Mail, LogOut, MapPin, Layout } from "lucide-react";
import Navbar from "../../components/Navbar/Navbar";
import MyAddress from "../../components/Client/MyProfile/MyAddress";
import MyOrders from "../../components/MyOrders/MyOrders";

import userServiceInstance from "../../APIs/users";
import { message, Modal, Form, Input, Button,  } from "antd";
import { useNavigate } from "react-router-dom";
import orderServiceInstance from "../../APIs/order";

const Loader: React.FC = () => {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Puff
        visible={true}
        height="60"
        width="60"
        color="#75A7AD"
        ariaLabel="puff-loading"
      />
    </div>
  );
};

interface AccountInfoCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
}

const AccountInfoCard: React.FC<AccountInfoCardProps> = ({
  title,
  value,
  icon,
}) => {
  return (
    <div className="bg-white rounded-sm shadow-sm p-4 flex items-center mb-4">
      <div className="bg-[#75A7AD] bg-opacity-10 p-3 rounded-full mr-4">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-gray-500 text-sm font-medium">{title}</p>
        <p className="text-gray-800 font-semibold">{value}</p>
      </div>
    </div>
  );
};

type TabType = "info" | "orders" | "address";

const MyAccount: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("info");
  const { user, logout } = useAuth();
  const [userDetails, setUserDetails] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const isAdminUser =
    userDetails?.role === "admin" || userDetails?.role === "super_admin";

  const navigateToDashboard = () => {
    navigate("/dashboard");
  };

  const fetchUserDetails = async () => {
    if (!user?.id) {
      console.warn("User ID is not defined.");
      return;
    }

    try {
      const result = await userServiceInstance.getUserById(user.id);
      if (result.data) {
        setUserDetails(result.data);
      } else {
        console.error("Failed to fetch user details:", result.error);
        message.error("Failed to fetch user details");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };


  const fetchOrders = async () => {
    try {
      const result = await orderServiceInstance.getOrderByUserId(user?.id || 0);
      if (result.data) {
        setOrders(result.data || []);
      
      }
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    }
   
  };



  useEffect(() => {
    if (user) {
      setUserDetails(user?.id);
      fetchOrders()
    }
    fetchUserDetails();
  }, [user?.id]);

  useEffect(() => {
    if (userDetails) {
      form.setFieldsValue({
        firstName: userDetails.firstName || "",
        lastName: userDetails.lastName || "",
        email: userDetails.email || "",
        phone_number: userDetails.phone_number || "",
      });
    }
  }, [userDetails, form]);

  const handleUpdateUser = async (values: any) => {
    setIsProcessing(true);

    try {
      if (!user?.id) {
        throw new Error("User ID is not defined");
      }

      const result = await userServiceInstance.editUser(user.id, values);

      if (result.data) {
        await fetchUserDetails();
        setIsModalVisible(false);
        message.success("Profile has been successfully updated!");
      } else {
        message.error(result.error || "Failed to update profile");
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
      message.error("Failed to update profile");
    } finally {
      setIsProcessing(false);
    }
  };

;

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!user || !userDetails) {
    return <Loader />;
  }

  const handleLogout = () => {
    logout();
    message.success("You have been successfully logged out");
    navigate("/");
  };


  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />

      {/* Ant Design Modal */}
      <Modal
        title="Edit Profile"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateUser}
          initialValues={{
            firstName: userDetails.firstName || "",
            lastName: userDetails.lastName || "",
            email: userDetails.email || "",
            phone_number: userDetails.phone_number || "",
          }}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[
              { required: true, message: "Please enter your first name" },
            ]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[
              { required: true, message: "Please enter your last name" },
            ]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}>
            <Input />
          </Form.Item>

          <Form.Item name="phone_number" label="Phone Number">
            <Input />
          </Form.Item>

          <Form.Item className="mb-0 flex justify-end">
            <Button
              onClick={() => setIsModalVisible(false)}
              style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isProcessing}
              style={{ backgroundColor: "#75A7AD", borderColor: "#75A7AD" }}>
              Update Profile
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <div className=" px-4 py-8 sm:pt-[13%] pt-[40%]">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Left sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-sm shadow-sm p-6 mb-6">
              <div className="flex flex-col items-center mb-6">
                <div className="bg-[#75A7AD] h-20 w-20 rounded-full flex items-center justify-center text-white mb-4">
                  <User size={36} />
                </div>
                <h2 className="text-xl font-bold">{`${userDetails.firstName} ${userDetails.lastName}`}</h2>
                <p className="text-gray-500">{userDetails.email}</p>
              </div>


              <div className="border-t pt-4">

              {isAdminUser && (
                <button
                  className="w-full text-left py-3 px-4 rounded-md flex items-center hover:bg-gray-100 text-[#75A7AD]"
                  onClick={navigateToDashboard}>
                  <Layout size={18} className="mr-3" />
                  <span>Dashboard</span>
                </button>
              )}
              
                <button
                  className={`w-full text-left py-3 px-4 rounded-md flex items-center ${
                    activeTab === "info"
                      ? "bg-[#75A7AD] bg-opacity-10 text-[#75A7AD]"
                      : "hover:bg-gray-100"
                  }`}
                  onClick={() => setActiveTab("info")}>
                  <User size={18} className="mr-3" />
                  <span>Account Information</span>
                </button>
                <button
                  className={`w-full text-left py-3 px-4 rounded-md flex items-center ${
                    activeTab === "orders"
                      ? "bg-[#75A7AD] bg-opacity-10 text-[#75A7AD]"
                      : "hover:bg-gray-100"
                  }`}
                  onClick={() => setActiveTab("orders")}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mr-3">
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="18"
                      rx="2"
                      ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span>My Orders</span>
                </button>
                <button
                  className={`w-full text-left py-3 px-4 rounded-md flex items-center ${
                    activeTab === "address"
                      ? "bg-[#75A7AD] bg-opacity-10 text-[#75A7AD]"
                      : "hover:bg-gray-100"
                  }`}
                  onClick={() => setActiveTab("address")}>
                  <MapPin size={18} className="mr-3" />
                  <span>My Address</span>
                </button>
                <button
                  className="w-full text-left py-3 px-4 rounded-md flex items-center hover:bg-gray-100 text-red-500"
                  onClick={() => handleLogout()}>
                  <LogOut size={18} className="mr-3" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            {activeTab === "info" && (
              <div className=" rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold">Account Information</h3>
                  <button
                    className="flex items-center text-[#75A7AD] hover:text-[#5a8389]"
                    onClick={() => setIsModalVisible(true)}>
                    <Edit size={16} className="mr-1" />
                    <span>Edit</span>
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <AccountInfoCard
                    title="First Name"
                    value={userDetails.firstName}
                    icon={<User size={20} className="text-[#75A7AD]" />}
                  />
                  <AccountInfoCard
                    title="Last Name"
                    value={userDetails.lastName}
                    icon={<User size={20} className="text-[#75A7AD]" />}
                  />
                  <AccountInfoCard
                    title="Email Address"
                    value={userDetails.email}
                    icon={<Mail size={20} className="text-[#75A7AD]" />}
                  />
                  <AccountInfoCard
                    title="Phone Number"
                    value={userDetails.phone_number || "-"}
                    icon={<Phone size={20} className="text-[#75A7AD]" />}
                  />
                </div>
              </div>
            )}

            {activeTab === "orders" && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-xl font-bold mb-6">My Ordersdd</h3>

                <MyOrders orders={orders} />
              </div>
            )}

            {activeTab === "address" && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold">My Address</h3>
                </div>
                <MyAddress />
              </div>
            )}
          </div>
        </div>
      </div>
 
    </div>
  );
};

export default MyAccount;